import { Box, Button, Divider } from '@wix/design-system';
import { SwitchProductModule } from '@wix/package-picker-sdk';
import { GtmEvents, useGtmLogger } from '@wix/package-picker-shared';
import {
  PageDisclaimer,
  TrustBuilders,
  useInitSelectPlanPageBiEvents,
  useIsStudioNewUi,
  useStudioPanorama,
} from '@wix/package-picker-shared-studio';
import { QueriesLoaderObserver } from '@wix/premium-fed-sdk/@apis-infra-react';
import { ScrollToTopButton } from '@wix/premium-purchase-platform/@ui';
import { ArrowUp } from '@wix/wix-ui-icons-common/odeditor';
import React from 'react';
import {
  AllPlansIncludedFeaturesBanner,
  FeaturesGrid,
  PageLayout,
  ProductCards,
} from '../../components';
import { AfnicTrustBuilder } from '../../components/AfnicTrustBuilder/AfnicTrustBuilder';
import { PlansSection } from '../../components/PlansSection';
import { TrustBuildersSection } from '../../components/TrustBuildersSection';
import { StudioPackagePickerModule } from '../../modules/studio-package-picker.module';
import s from './SelectPlanPage.scss';
import { SelectPlanPageLoader } from './SelectPlanPageLoader';

const SECTION_GAP = '72px';

export const SelectPlanPage = () => {
  const { t } = StudioPackagePickerModule.internals.useTranslations();
  const panorama = useStudioPanorama();
  const gridRef = React.useRef<HTMLDivElement>(null);
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const { didInit } = useInitSelectPlanPageBiEvents();
  const didReportLoad = React.useRef(false);
  const isStudioNewUi = useIsStudioNewUi();
  useGtmLogger(GtmEvents.SELECT_PLAN);

  const {
    isDowngradeFlow,
    isUpgradeFlow,
    isLoading: isSwitchFlowsLoading,
  } = SwitchProductModule.SDK.hooks.useSwitchFlows();

  React.useEffect(() => {
    if (!isSwitchFlowsLoading) {
      if (isUpgradeFlow) {
        document.title = t('seo.studio.second_upgrade.title');
      } else if (isDowngradeFlow) {
        document.title = t('seo.studio.change_plan.app_title');
      }
    }
  }, [isDowngradeFlow, isUpgradeFlow, isSwitchFlowsLoading]);

  React.useEffect(() => {
    panorama.startTransaction('select-plan-page-load');
  }, []);

  return (
    <QueriesLoaderObserver
      Loading={<SelectPlanPageLoader />}
      onFinishLoading={() => {
        if (didReportLoad.current || !didInit) {
          return;
        }
        StudioPackagePickerModule.internals.bi.onSelectPlanPageLoad?.({});
        panorama.finishTransaction('select-plan-page-load');
        didReportLoad.current = true;
      }}
    >
      {isStudioNewUi ? (
        <PageLayout>
          <PlansSection gridRef={gridRef} scrollRef={scrollRef} />
          <Box width="100%" marginTop={SECTION_GAP}>
            <AllPlansIncludedFeaturesBanner />
          </Box>
          <Box width="100%" align="center" marginTop={SECTION_GAP}>
            <FeaturesGrid innerRef={gridRef} />
          </Box>
          <TrustBuildersSection>
            <AfnicTrustBuilder />
          </TrustBuildersSection>
          <ScrollToTopButton
            className={s.newUiScrollToTop}
            Icon={<ArrowUp />}
            componentRef={scrollRef}
            text={t('studio_package_picker.table.go_to_top')}
            onClick={() => {
              StudioPackagePickerModule.internals.bi.onScrollToTopClick?.({});
            }}
          />
        </PageLayout>
      ) : (
        <PageLayout>
          <Box width="100%" align="center" marginTop="72px">
            <ProductCards />
          </Box>
          <Box marginTop="SP5">
            <Button
              skin="dark"
              priority="secondary"
              onClick={() => {
                gridRef.current?.scrollIntoView({ behavior: 'smooth' });
                StudioPackagePickerModule.internals.bi.onComparePlanFeaturesClick?.(
                  {},
                );
              }}
            >
              {t('premium-purchase-plan.cards.compare_features.cta')}
            </Button>
          </Box>
          <div ref={scrollRef} />
          <Box width="100%" margin="SP5 0">
            <AllPlansIncludedFeaturesBanner />
          </Box>
          <Box
            width="100%"
            marginTop="SP3"
            maxWidth="1140px"
            direction="vertical"
            gap="SP2"
          >
            {/* TODO: Move to reusable desktop component */}
            <Box direction="vertical" gap="30px">
              <PageDisclaimer />
              <Divider />
              <TrustBuilders />
            </Box>
            <AfnicTrustBuilder />
          </Box>
          <Box width="100%" align="center" maxWidth="1140px" marginTop="SP7">
            <FeaturesGrid innerRef={gridRef} />
          </Box>
          <ScrollToTopButton
            componentRef={scrollRef}
            text={t('studio_package_picker.table.go_to_top')}
            onClick={() => {
              StudioPackagePickerModule.internals.bi.onScrollToTopClick?.({});
            }}
          />
        </PageLayout>
      )}
    </QueriesLoaderObserver>
  );
};
